import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  TrendingUp,
  TrendingDown,
  Newspaper,
  RefreshCw,
  Loader2,
  ChevronLeft,
  Lightbulb,
  MessageSquare,
  Send,
  Calendar,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Globe,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import FunFactsLoader from "./FunFactsLoader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailyMarketUpdate = ({ onBack, hideInternalBackButton = false }) => {
  const [marketData, setMarketData] = useState(null);
  const [accurateMovers, setAccurateMovers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentAuthor, setCommentAuthor] = useState("");
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [personalizedTip, setPersonalizedTip] = useState(null);

  useEffect(() => {
    loadMarketUpdate();
    loadAccurateMovers();
    checkAdmin();
    loadPersonalizedTip();
  }, []);

  const checkAdmin = () => {
    const token = localStorage.getItem("compoundr_admin_token");
    setIsAdmin(!!token);
  };

  const loadMarketUpdate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/market-update/today`);
      setMarketData(response.data);
    } catch (error) {
      toast.error("Failed to load market update");
    } finally {
      setLoading(false);
    }
  };

  const loadAccurateMovers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-movers`);
      if (response.data.success) {
        setAccurateMovers(response.data);
      }
    } catch (error) {
      console.error("Failed to load accurate market movers");
    }
  };

  const loadPersonalizedTip = async () => {
    const userData = localStorage.getItem("user_data") || sessionStorage.getItem("user_data");
    if (userData) {
      const user = JSON.parse(userData);
      try {
        const response = await axios.get(`${API_URL}/api/tips/personalized/${user.id}?context=market-update`);
        setPersonalizedTip(response.data);
      } catch (error) {
        console.error("Failed to load tip");
      }
    }
  };

  const submitFounderComment = async () => {
    if (!commentAuthor.trim() || !commentText.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem("compoundr_admin_token");
      await axios.post(
        `${API_URL}/api/market-update/founder-comment`,
        { author: commentAuthor, comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comment added successfully!");
      setCommentAuthor("");
      setCommentText("");
      setShowCommentForm(false);
      loadMarketUpdate();
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <FunFactsLoader message="Loading market insights..." />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6" data-testid="daily-market-update">
      {/* Header with Prominent Date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {!hideInternalBackButton && (
            <Button variant="ghost" onClick={onBack} data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Daily Market Update</h1>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="w-5 h-5 text-emerald-600" />
              <span className="text-lg font-semibold text-emerald-700">
                {marketData?.formatted_date || new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={loadMarketUpdate}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Live Index Ticker */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-slate-500 mb-1">S&P 500</p>
          <p className="text-lg font-bold text-slate-800">
            {(accurateMovers?.indices?.["S&P 500"]?.value || marketData?.sp500_data?.index_value)?.toLocaleString() || "—"}
          </p>
          <p className={`text-sm font-medium ${
            (accurateMovers?.indices?.["S&P 500"]?.direction || marketData?.sp500_data?.change_direction) === 'up' ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {(accurateMovers?.indices?.["S&P 500"]?.direction || marketData?.sp500_data?.change_direction) === 'up' ? '↑' : '↓'} {Math.abs(accurateMovers?.indices?.["S&P 500"]?.change_pct || marketData?.sp500_data?.change_pct || 0).toFixed(2)}%
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-slate-500 mb-1">TSX Composite 🇨🇦</p>
          <p className="text-lg font-bold text-slate-800">
            {(accurateMovers?.indices?.["TSX Composite"]?.value || marketData?.tsx_data?.index_value)?.toLocaleString() || "—"}
          </p>
          <p className={`text-sm font-medium ${
            (accurateMovers?.indices?.["TSX Composite"]?.direction || marketData?.tsx_data?.change_direction) === 'up' ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {(accurateMovers?.indices?.["TSX Composite"]?.direction || marketData?.tsx_data?.change_direction) === 'up' ? '↑' : '↓'} {Math.abs(accurateMovers?.indices?.["TSX Composite"]?.change_pct || marketData?.tsx_data?.change_pct || 0).toFixed(2)}%
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-slate-500 mb-1">NASDAQ</p>
          <p className="text-lg font-bold text-slate-800">
            {accurateMovers?.indices?.["NASDAQ"]?.value?.toLocaleString() || "—"}
          </p>
          <p className={`text-sm font-medium ${
            accurateMovers?.indices?.["NASDAQ"]?.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {accurateMovers?.indices?.["NASDAQ"]?.direction === 'up' ? '↑' : '↓'} {Math.abs(accurateMovers?.indices?.["NASDAQ"]?.change_pct || 0).toFixed(2)}%
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-slate-500 mb-1">CAD/USD</p>
          <p className="text-lg font-bold text-slate-800">
            ${marketData?.tsx_data?.cad_usd?.toFixed(4) || "0.7400"}
          </p>
          <p className="text-xs text-slate-500">Exchange Rate</p>
        </div>
      </div>

      {/* Personalized Tip */}
      {personalizedTip && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-purple-800">{personalizedTip.tip_title}</h4>
              <p className="text-sm text-purple-700">{personalizedTip.tip_content}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Market Summary */}
      <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2">Market Overview</h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                {marketData?.market_summary || "Markets showed mixed activity today."}
              </p>
            </div>
          </div>

          {/* Index Summaries */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-sm text-emerald-400 font-semibold mb-1">🇨🇦 TSX Composite</p>
              <p className="text-white text-sm">{marketData?.tsx_summary || "Trading steady"}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-sm text-blue-400 font-semibold mb-1">🇺🇸 S&P 500</p>
              <p className="text-white text-sm">{marketData?.sp500_summary || "Trading steady"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Headlines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-blue-600" />
            Today&apos;s Headlines
          </CardTitle>
          <p className="text-sm text-slate-500">Real-time market news - both good and bad</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {marketData?.headlines?.map((headline, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-start gap-3 p-4 rounded-lg border-l-4 ${
                  headline.impact === "positive"
                    ? "bg-emerald-50 border-emerald-500"
                    : headline.impact === "negative"
                    ? "bg-red-50 border-red-500"
                    : "bg-slate-50 border-slate-400"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  headline.impact === "positive"
                    ? "bg-emerald-100"
                    : headline.impact === "negative"
                    ? "bg-red-100"
                    : "bg-slate-100"
                }`}>
                  {headline.impact === "positive" ? (
                    <ArrowUp className="w-4 h-4 text-emerald-600" />
                  ) : headline.impact === "negative" ? (
                    <ArrowDown className="w-4 h-4 text-red-600" />
                  ) : (
                    <span className="text-slate-500 text-xs">•</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${
                    headline.impact === "positive"
                      ? "text-emerald-800"
                      : headline.impact === "negative"
                      ? "text-red-800"
                      : "text-slate-800"
                  }`}>{headline.title}</p>
                  <p className="text-sm text-slate-600 mt-1">{headline.explanation}</p>
                </div>
                <Badge
                  className={`flex-shrink-0 ${
                    headline.impact === "positive"
                      ? "bg-emerald-500"
                      : headline.impact === "negative"
                      ? "bg-red-500"
                      : "bg-slate-500"
                  }`}
                >
                  {headline.impact}
                </Badge>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Movers */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Gainers */}
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <TrendingUp className="w-5 h-5" />
              Top Gainers
              {accurateMovers && (
                <Badge variant="outline" className="text-xs ml-2 bg-emerald-50">Live Data</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(accurateMovers?.top_gainers?.length > 0 ? accurateMovers.top_gainers.slice(0, 5) : marketData?.top_gainers)?.map((stock, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-800">{stock.symbol}</span>
                      <Badge className="bg-emerald-500 text-xs">
                        <ArrowUp className="w-3 h-3 mr-1" />+{typeof stock.change_pct === 'number' ? stock.change_pct.toFixed(2) : stock.change_pct}%
                      </Badge>
                      {stock.currency && (
                        <Badge variant="outline" className="text-xs">{stock.currency === "CAD" ? "🇨🇦" : "🇺🇸"}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{stock.name}</p>
                    {stock.price && (
                      <p className="text-xs text-emerald-700 mt-1">${stock.price.toFixed(2)} {stock.currency || ""}</p>
                    )}
                    {stock.reason && (
                      <p className="text-xs text-slate-500 mt-1">{stock.reason}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              {(!accurateMovers?.top_gainers?.length && !marketData?.top_gainers?.length) && (
                <p className="text-sm text-slate-500 text-center py-4">No significant gainers today</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Losers */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <TrendingDown className="w-5 h-5" />
              Top Losers
              {accurateMovers && (
                <Badge variant="outline" className="text-xs ml-2 bg-red-50">Live Data</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(accurateMovers?.top_losers?.length > 0 ? accurateMovers.top_losers.slice(0, 5) : marketData?.top_losers)?.map((stock, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-800">{stock.symbol}</span>
                      <Badge className="bg-red-500 text-xs">
                        <ArrowDown className="w-3 h-3 mr-1" />{typeof stock.change_pct === 'number' ? stock.change_pct.toFixed(2) : stock.change_pct}%
                      </Badge>
                      {stock.currency && (
                        <Badge variant="outline" className="text-xs">{stock.currency === "CAD" ? "🇨🇦" : "🇺🇸"}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{stock.name}</p>
                    {stock.price && (
                      <p className="text-xs text-red-700 mt-1">${stock.price.toFixed(2)} {stock.currency || ""}</p>
                    )}
                    {stock.reason && (
                      <p className="text-xs text-slate-500 mt-1">{stock.reason}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              {(!accurateMovers?.top_losers?.length && !marketData?.top_losers?.length) && (
                <p className="text-sm text-slate-500 text-center py-4">No significant losers today</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Beginner's Tip */}
      {marketData?.beginners_tip && (
        <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-amber-800 mb-1">
                  Beginner&apos;s Tip: {marketData.beginners_tip.title}
                </h3>
                <p className="text-amber-700 text-sm">{marketData.beginners_tip.content}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Founder Comments - Always visible */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Founder&apos;s Take
          </CardTitle>
          {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCommentForm(!showCommentForm)}
                data-testid="add-comment-btn"
              >
                {showCommentForm ? "Cancel" : "Add Comment"}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {/* Comment Form */}
            <AnimatePresence>
              {showCommentForm && isAdmin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 bg-purple-50 rounded-lg"
                >
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Your Name</label>
                      <Input
                        placeholder="e.g., Arsh Dharani"
                        value={commentAuthor}
                        onChange={(e) => setCommentAuthor(e.target.value)}
                        className="mt-1"
                        data-testid="comment-author-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Your Thoughts</label>
                      <Textarea
                        placeholder="Share your take on today's market..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        rows={3}
                        className="mt-1"
                        data-testid="comment-text-input"
                      />
                    </div>
                    <Button
                      onClick={submitFounderComment}
                      disabled={submittingComment}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="submit-comment-btn"
                    >
                      {submittingComment ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Post Comment
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Existing Comments */}
            {marketData?.founder_comments?.length > 0 ? (
              <div className="space-y-4">
                {marketData.founder_comments.map((comment, index) => (
                  <div key={comment.id || index} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 font-bold text-sm">
                          {comment.author?.charAt(0) || "F"}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{comment.author}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(comment.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <p className="text-slate-700">{comment.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="font-medium">Check back soon!</p>
                <p className="text-sm mt-1">Our founders share their market insights here daily.</p>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Disclaimer */}
      <div className="text-center text-xs text-slate-500 px-4 py-6">
        <p>
          This market update is generated for educational purposes only and should not be considered
          financial advice. Always do your own research before making investment decisions.
        </p>
      </div>
    </div>
  );
};

export default DailyMarketUpdate;
